// DS2.5.3, DS2.5.3M
import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import PlainButtonOrLink from '../../Buttons/PlainButtonOrLink';
import CommentFill16 from '../../Icons/16x16/Comment';
import CommentOutline16 from '../../Icons/16x16/CommentOutline';
import FavStarOutline16 from '../../Icons/16x16/FavStar';
import FavStarFill16 from '../../Icons/16x16/FavStarFill';
import CommentFill24 from '../../Icons/24x24/Comment';
import CommentOutline24 from '../../Icons/24x24/CommentOutline';
import FavStarOutline24 from '../../Icons/24x24/FavStar';
import FavStarFill24 from '../../Icons/24x24/FavStarFill';
import { IconSize, Props as IconProps } from '../../Icons/IconWrap';
import shortenNumber from '../../../utils/shortenNumber';
import { setRef } from '../../../utils/refHelpers';
import { sanitizeHref } from "@wix/da-url";

import s from './ThumbIconButton.scss';

interface Props {
  type: 'comment' | 'fave';
  isEngaged?: boolean;
  isFakeButton?: boolean;
  iconSizeOverride?: IconSize;
  iconShapeSize?: IconSize.MEDIUM /* 24x24 */ | IconSize.SMALLER /* 16x16 */;
  showStatValue?: boolean;
  statValue?: number;
  className?: string;
  'aria-label'?: string;
  href?: string;
  biData?: BiEvent;
  onClick?: (e: React.MouseEvent) => void;
}

const ThumbIconButton = React.forwardRef<HTMLElement | null, Props>(
  (
    {
      type,
      isEngaged = false,
      isFakeButton = false,
      iconSizeOverride,
      statValue = 0,
      showStatValue = true,
      'aria-label': ariaLabel,
      className,
      href,
      biData,
      onClick,
      iconShapeSize = IconSize.SMALLER,
    },
    ref
  ) => {
    const isMobile = useContext(MobileContext);

    const icon = useMemo(() => {
      const FaveIconFill =
        iconShapeSize === IconSize.MEDIUM ? FavStarFill24 : FavStarFill16;
      const FaveIconOutline =
        iconShapeSize === IconSize.MEDIUM ? FavStarOutline24 : FavStarOutline16;

      const CommentIconFill =
        iconShapeSize === IconSize.MEDIUM ? CommentFill24 : CommentFill16;
      const CommentIconOutline =
        iconShapeSize === IconSize.MEDIUM ? CommentOutline24 : CommentOutline16;

      const iconProps: IconProps = {
        size:
          typeof iconSizeOverride !== 'undefined'
            ? iconSizeOverride
            : isMobile
              ? IconSize.MEDIUM
              : IconSize.SMALLER,
        disableDefaultColors: true,
        className: s['icon'],
      };
      switch (type) {
        case 'comment':
          return isEngaged ? (
            <CommentIconFill {...iconProps} />
          ) : (
            <CommentIconOutline {...iconProps} />
          );
        case 'fave':
          return isEngaged ? (
            <FaveIconFill {...iconProps} />
          ) : (
            <FaveIconOutline {...iconProps} />
          );
        default:
          return null;
      }
    }, [isEngaged, isMobile, iconSizeOverride, type]);

    return (
      <PlainButtonOrLink
        innerRef={node => setRef(ref, node)}
        href={sanitizeHref(href)}
        onClick={onClick}
        isFakeButton={isFakeButton}
        className={classnames(s['root'], className)}
        biData={biData}
        aria-label={ariaLabel}
      >
        {icon}
        {showStatValue && (
          <span className={s['value']}>{shortenNumber(statValue)}</span>
        )}
      </PlainButtonOrLink>
    );
  }
);
ThumbIconButton.displayName = 'ThumbIconButton';

export default ThumbIconButton;
